import { SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useDialogs } from './useDialogs';

const SAVE_DELAY = 500;

const readFromStorage = <T>(key: string, fallback: T): T => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch {
    return fallback;
  }
};

const useDebouncedLocalStorage = <T>(key: string, fallback: T) => {
  const { showError } = useDialogs();
  const [value, setValue] = useState<T>(() => readFromStorage(key, fallback));
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);
  const latestRef = useRef(value);

  useEffect(() => {
    latestRef.current = value;
  }, [value]);

  const setValueAndPersist = useCallback(
    (newValue: SetStateAction<T>) => {
      setValue(newValue);

      clearTimeout(timeoutRef.current ?? undefined);
      timeoutRef.current = setTimeout(() => {
        try {
          localStorage.setItem(key, JSON.stringify(latestRef.current));
        } catch (error) {
          console.error(`Failed to save ${key}:`, error);
          showError({
            title: `Failed to save ${key}`,
            message: error?.toString() || `Unknown error occurred when saving ${key} to LocalStorage`
          });
        }
      }, SAVE_DELAY);
    },
    [key]
  );

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current ?? undefined);
      try {
        localStorage.setItem(key, JSON.stringify(latestRef.current));
      } catch (e) {
        console.error(`Failed to save ${key} on unmount:`, e);
      }
    };
  }, [key]);

  return [value, setValueAndPersist] as const;
};

export default useDebouncedLocalStorage;
